import type { ReportItem, ReportSection } from '@neo/blocks/sidebar/app-sidebar-reports'
import {
  Activity,
  CircleDollarSign,
  Clock,
  Gauge,
  LayoutDashboard,
  Megaphone,
  MessagesSquare,
  Mic,
  TrendingUp,
  Waypoints,
} from 'lucide-react'
import {
  canViewAgents,
  canViewReports,
  canViewUsageReports,
  type RouteMeta,
} from 'v2/shared/utils/role-based-visibility'

// AI-agent reports. Behind the `voiceAgentsSplit` flag the Voice Agent report is split into focused
// sub-reports (Overview / AI Performance / Outcomes; Overview also carries conversation quality).
// Without the flag it stays a single "Voice Agent" item. Sibling URLs are kept non-prefix-
// overlapping so the startsWith() active check stays unambiguous.
const AI_AGENT_ITEMS_SPLIT: ReportItem[] = [
  { title: 'Overview', url: '/analytics/voice-agents/overview', icon: LayoutDashboard },
  { title: 'AI Agent Performance', url: '/analytics/voice-agents/ai-performance', icon: Gauge },
  { title: 'Outcomes & Goals', url: '/analytics/voice-agents/outcomes', icon: Waypoints },
  { title: 'Campaign & Performance', url: '/analytics/voice-agents/campaign', icon: Megaphone },
]

const AI_AGENT_ITEMS_LEGACY: ReportItem[] = [
  { title: 'Voice Agent', url: '/analytics/voice-agents', icon: Mic },
]

// Human-specialist + general reports. (Names are intentionally unchanged for now — the
// section split is what's shipping; renames come later.)
const HUMAN_SPECIALIST_ITEMS: ReportItem[] = [
  { title: 'Customer Metrics', url: '/analytics/customer-metrics', icon: TrendingUp },
  {
    title: 'Conversations Overview',
    url: '/analytics/conversations-overview',
    icon: MessagesSquare,
  },
  { title: 'Specialist Performance', url: '/analytics/agent-performance', icon: Activity },
  { title: 'Specialist Availability', url: '/analytics/agent-availability', icon: Clock },
  { title: 'Operations Overview', url: '/analytics/operations-overview', icon: LayoutDashboard },
]

// Usage (billed volume/spend from UsageSummary). Three separate reports, each a route-driven tab
// of the same page (/analytics/usage/:tab). Overview is the default landing report.
const USAGE_ITEMS: ReportItem[] = [
  { title: 'Overall Spend', url: '/analytics/usage/overview', icon: CircleDollarSign },
  { title: 'Messaging', url: '/analytics/usage/messaging', icon: MessagesSquare },
  { title: 'Voice', url: '/analytics/usage/voice', icon: Mic },
]

// AI Agents on top, Human Specialist next, Usage last, each only when the session has it. The
// sidebar, the /analytics route gate and the /analytics landing all read this list, so they agree.
export const getReportSections = (meta?: RouteMeta): ReportSection[] => {
  if (!canViewReports(meta)) return []
  const sections: ReportSection[] = []
  if (canViewAgents(meta)) {
    const aiItems = meta?.featureFlags?.voiceAgentsSplitEnabled
      ? AI_AGENT_ITEMS_SPLIT
      : AI_AGENT_ITEMS_LEGACY
    sections.push({ label: 'AI Agents', items: aiItems })
  }
  if (meta?.profileData?.organizations?.[0]?.human_specialist_enabled) {
    sections.push({ label: 'Human Specialist', items: HUMAN_SPECIALIST_ITEMS })
  }
  if (canViewUsageReports(meta)) sections.push({ label: 'Usage', items: USAGE_ITEMS })
  return sections
}

// /analytics lands on the first item the sidebar lists. With no section the route is denied, so
// the fallback only covers a state the gate already rejects.
export const getReportsLandingPath = (sections: ReportSection[]): string =>
  sections[0]?.items[0]?.url ?? '/analytics/customer-metrics'
